import { useEffect, useMemo, useRef, useState } from 'react'

import type { LoggbokSpor, SporFartoy } from '../api'
import { penFeature, radiusM, structureFeature, toLocal } from '../merdgeo'
import { newPlayhead, playheadEpoch, type Playhead } from '../playhead'
import { createSegmentPlayer, type PlayerState, type Segment } from '../segments'
import { startSverm, type Sverm } from '../sverm'
import { ErrorBox, Rows } from './bits'
import { useApi } from '../useApi'

/* Å SE ET BESØK, framfor å lese en setning om det.
 *
 * «"ANDREAS" lå ved anlegget 00:00-12:00, nærmest 78 m» er sant og lar seg
 * ikke svare på. Tolv timer er en avlusing, en henting, en dykker, en notvask
 * og en båt som fikk motorstopp, og den som blir spurt hva som skjedde må velge
 * én. Sporet svarer uten ord: en linje som går inn, står stille ved merd 4 i
 * seks timer og går ut igjen er en annen hendelse enn en som tråler hele rekka
 * i tre knop, og en driftsleder ser hvilken på omtrent ett sekund.
 *
 * ---------------------------------------------------------------------------
 * ÉN KLOKKE, ÉN FART.
 *
 * Det fristende her er to hastigheter: lyden i sanntid og båtene i hurtigspoling
 * så tolv timer får plass i tolv sekunder. Da lyver bildet. En båt som tegnes i
 * seksti ganger farten «passerer» der den i virkeligheten lå, og forskjellen
 * mellom å ligge og å passere er hele grunnen til at kartet er her.
 *
 * Så: ett sekund lyd er ett sekund bevegelse -- samme regel som timekartet i
 * loggen. At hele besøket likevel kan overskues, er skinnas jobb og ikke
 * avspillingens: linja for hele dagen ligger der ferdig tegnet, nivåkurven sier
 * hvor det er noe å høre, og et drag med tommelen flytter båten dit.
 *
 * ---------------------------------------------------------------------------
 * ALDRI EKSTRAPOLERT.
 *
 * Mellom to AIS-meldinger interpoleres det; utenfor sporets ender skjules
 * merket. En båt tegnet ved merdene klokka 03 fordi den kom klokka 09 er en
 * påstand vi ikke har dekning for, og den ville vært usynlig som feil nettopp
 * fordi den beveger seg troverdig.
 */

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

/* Hardkodet framfor lest fra tokens i styles.css, av samme grunn som
   Anleggskart: MapLibre regner farger i WebGL og ser ingen CSS-variabel.
   Holdt i takt for hånd og navngitt etter tokenet de speiler. */
const C_SIGNAL = '#b4d9c7'    // --signal
const C_DEMPET = '#93ab9f'    // en båt som ikke er den kortet spør om
const C_RING = '#e6f1ea'      // --text

/** Så nær anlegget må et sporpunkt være for å få sette ramma.
 *
 *  Åtte hundre meter: langt nok til at innseilinga og manøvreringa er med, kort
 *  nok til at merdene fyller bildet. Uten en slik grense rammer et døgnspor
 *  hele fjorden, og da er ringene en flekk. */
const RAMME_M = 800

/** Minuttet er lydens egen lengde her: `reduced` er ett minutts proxy, og det
 *  er den eneste kilden dette panelet spør etter. */
const MINUTT_S = 60

/** Skroget sett ovenfra, baugen i nord.
 *
 *  EN TREKANT VAR IKKE NOK, og det var ikke en smakssak. Timekartet i loggen
 *  tegner fartøy som en trekant, og på et satellittbilde med sju ringer og en
 *  flåte leses den som en markør blant markører -- «det er et eller annet der»,
 *  ikke «det er en båt der». Et skrog med spiss baug, utoverbøyde sider og
 *  flatt akterspeil leses som en båt uten at noen forklarer det, og retningen
 *  følger med på kjøpet: du ser hvilken vei den lå.
 *
 *  Styrhus og mast er med fordi de er det som skiller en arbeidsbåt fra et
 *  blad. De forsvinner under omtrent 16 piksler, og da bærer skroget alene. */
const SKROG = 'M12 2 C9 5.5 7.5 10 7.5 14.5 L7.5 20.5'
  + ' C7.5 21.3 8.2 22 9 22 L15 22 C15.8 22 16.5 21.3 16.5 20.5'
  + ' L16.5 14.5 C16.5 10 15 5.5 12 2 Z'

function skipSvg(px: number): string {
  return `<svg viewBox="0 0 24 24" width="${px}" height="${px}"`
    + ' aria-hidden="true" focusable="false">'
    + `<path d="${SKROG}" fill="currentColor" stroke="rgba(4,21,15,.85)"`
    + ' stroke-width="1.1" stroke-linejoin="round"/>'
    + '<rect x="9.6" y="11" width="4.8" height="5.6" rx="0.8"'
    + ' fill="rgba(4,21,15,.5)"/>'
    + '<rect x="10.8" y="6.5" width="2.4" height="3" rx="0.6"'
    + ' fill="rgba(4,21,15,.35)"/></svg>'
}

function klokkeslett(epoch: number, tz: string | null | undefined): string {
  return new Date(epoch * 1000).toLocaleTimeString('nb-NO',
    { hour: '2-digit', minute: '2-digit', timeZone: tz || undefined })
}

function avstand(m: number | null | undefined): string {
  if (m == null) return ''
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`
}

/** Hvor båten var ved `t`, eller null når klokka står utenfor sporet.
 *
 *  Null og ikke nærmeste ende: kallerens to tilfeller er «tegn den her» og
 *  «la den stå», og en funksjon som svarer med et punkt uansett gjør det umulig
 *  å skille dem. */
function posisjon(spor: SporFartoy['track'], t: number)
: [number, number] | null {
  if (spor.length === 0) return null
  if (t < spor[0]![0] || t > spor[spor.length - 1]![0]) return null
  for (let i = 1; i < spor.length; i++) {
    const b = spor[i]!
    if (t <= b[0]) {
      const a = spor[i - 1]!
      const spenn = b[0] - a[0]
      const k = spenn > 0 ? (t - a[0]) / spenn : 0
      return [a[2] + (b[2] - a[2]) * k, a[1] + (b[1] - a[1]) * k]
    }
  }
  return [spor[spor.length - 1]![2], spor[spor.length - 1]![1]]
}

/** Kursen fra siste AIS-melding før `t`. Reserveløsningen, ikke hovedsvaret --
 *  se `kursVed`. */
function meldtKurs(spor: SporFartoy['track'], t: number): number | null {
  let kurs: number | null = null
  for (const p of spor) {
    if (p[0] > t) break
    if (p[4] != null) kurs = p[4]
  }
  return kurs
}

/** Hvor langt båten må ha flyttet seg for at en peiling betyr noe. */
const KURS_M = 15
/** ...og hvor fort, i meter i sekundet. Terskelen må VOKSE med vinduet: femten
 *  meter på tre kvarters vindu er en fortøyd båt som driver, og en peiling
 *  regnet av drift peker en tilfeldig vei. Rundt 0,8 knop -- under det seiler
 *  ingen, og da er siste meldte kurs det ærligere svaret. */
const KURS_FART = 0.4
/** Vinduene rundt klokkeslettet, i sekunder, prøvd i tur. */
const KURS_VINDU = [45, 120, 300, 600]

/** Hvilken vei baugen peker ved `t`.
 *
 *  FRA LINJA, ikke fra AIS-meldingens `cog`, og det er en omvending av det som
 *  sto her før. Meldingen er båtens egen kurs og burde vært det sanneste
 *  svaret. Men den kommer hvert femte minutt: mellom to meldinger står tallet
 *  stille mens streken svinger, og etter fortøyning blir det stående på den
 *  kursen hun hadde da hun kom inn. Resultatet var en baug som pekte øst mens
 *  sporet gikk sørvest -- kartet motsa seg selv på samme piksel. Da vinner
 *  tegningen, fordi tegningen er det man ser.
 *
 *  Vinduet vokser til hun har flyttet seg langt nok til at retningen betyr
 *  noe. En fortøyd båt driver noen meter fram og tilbake, og en peiling regnet
 *  av den snurrer rundt; der er siste meldte kurs bedre, og den står i det
 *  minste stille. */
function kursVed(spor: SporFartoy['track'], t: number,
                 raa: SporFartoy['track'] = spor): number | null {
  if (spor.length < 2) return null
  const fra = spor[0]![0]
  const til = spor[spor.length - 1]![0]
  for (const w of KURS_VINDU) {
    const ta = Math.max(fra, t - w)
    const tb = Math.min(til, t + w)
    const a = posisjon(spor, ta)
    const b = posisjon(spor, tb)
    if (!a || !b || tb <= ta) continue
    const dy = (b[1] - a[1]) * 110540
    const dx = (b[0] - a[0]) * 111320 * Math.cos(a[1] * Math.PI / 180)
    if (Math.hypot(dx, dy) < Math.max(KURS_M, KURS_FART * (tb - ta))) continue
    return (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360
  }
  /* Reserven leser DET RÅ sporet. `tynn` kaster punktene båten lå stille
     mellom, og med dem kursen hun meldte mens hun lå der -- så et fortøyd
     fartøy ville pekt den veien hun kom inn, ikke den veien hun faktisk lå. */
  return meldtKurs(raa, t)
}

/* ---------------------------------------------------------------------------
 * SPORET ER IKKE GLATT, OG DET KAN IKKE BLI DET AV SEG SELV.
 *
 * AIS-en har tre til seks minutter mellom meldingene -- det er KILDEN som er
 * slik, ikke lagringen vår: `api.phoniq.no/v1/ais/passing` gir 112 punkter på
 * ti timer for ANDREAS, median 359 sekunder. En båt i ti knop flytter seg
 * halvannen kilometer mellom to meldinger, og en rett strek mellom dem tegnet
 * et mangekantet omriss med skarpe hjørner: en polygon, ikke en seilas.
 *
 * Så linja glattes her, etter to regler som gjør at den fortsatt er sann.
 *
 * 1. HVERT MÅLTE PUNKT LIGGER PÅ KURVEN. Catmull-Rom går gjennom punktene
 *    sine. Et glidende snitt og Chaikin skjærer hjørnene og flytter båten vekk
 *    fra der hun faktisk var, og det er en annen påstand enn den vi har. Det
 *    som er oppdiktet er bare veien MELLOM to observasjoner, og der er en bue
 *    nærmere sannheten enn et knekkpunkt: ingen båt svinger på stedet.
 *
 * 2. SAMME KURVE BÆRER BÅDE STREKEN OG BÅTEN. Å tegne en glattet strek og la
 *    merket følge de rå punktene ville latt båten skjære innafor sin egen
 *    linje i hver sving -- to svar på hvor hun var, på samme skjerm.
 *
 * Sentripetal (alpha = 0.5) og ikke uniform: punktene står ujevnt, og en
 * uniform Catmull-Rom lager løkker og overskyt nettopp der avstanden mellom to
 * meldinger er ulik -- altså overalt her.
 *
 * KLOKKA FØLGER IKKE MED PÅ KURVEN. Tidspunktet på et innskutt punkt regnes
 * rett mellom naboene, ikke gjennom splinen. Vi vet ingenting om farten mellom
 * to meldinger, og en spline på tidsaksen kan gå baklengs.
 */

/** Hvor nær to punkter må være for at det andre er samme sted som det første. */
const STILLE_M = 8
/** Hvor langt et strekk må være før det deles, og i hvor mange biter. */
const BUE_M = 60
const BUE_MAX = 10

type Punkt = SporFartoy['track'][number]
interface XY { t: number; lat: number; lon: number }

const somXY = (p: Punkt): XY => ({ t: p[0], lat: p[1], lon: p[2] })

function mellom(a: XY, b: XY): number {
  const dy = (b.lat - a.lat) * 110540
  const dx = (b.lon - a.lon) * 111320 * Math.cos(a.lat * Math.PI / 180)
  return Math.hypot(dx, dy)
}

const speil = (a: XY, b: XY): XY =>
  ({ t: 2 * a.t - b.t, lat: 2 * a.lat - b.lat, lon: 2 * a.lon - b.lon })

const bland = (a: XY, b: XY, k: number): XY => ({
  t: a.t + (b.t - a.t) * k,
  lat: a.lat + (b.lat - a.lat) * k,
  lon: a.lon + (b.lon - a.lon) * k,
})

/** Vekk med punktene båten ikke flyttet seg mellom.
 *
 *  En fortøyd båt sender like fullt hvert femte minutt, og de ti timene
 *  ANDREAS lå ved flåten er hundre punkter innenfor to meter av hverandre. De
 *  bærer ingen opplysning om vei, og gjennom en kurve blir de en floke.
 *
 *  MEN DET SISTE PUNKTET FØR HUN GÅR MÅ BLI MED. Uten det interpolerer klokka
 *  rett fra ankomst til avgang, og båten sklir sakte sidelengs i ti timer i
 *  stedet for å ligge stille og så dra. */
function tynn(spor: Punkt[]): Punkt[] {
  if (spor.length < 3) return spor
  const ut: Punkt[] = [spor[0]!]
  for (let i = 1; i < spor.length - 1; i++) {
    const p = spor[i]!
    const flyttet = mellom(somXY(ut[ut.length - 1]!), somXY(p)) >= STILLE_M
    const skalFlytte = mellom(somXY(p), somXY(spor[i + 1]!)) >= STILLE_M
    if (flyttet || skalFlytte) ut.push(p)
  }
  ut.push(spor[spor.length - 1]!)
  return ut
}

/** Sporet som en kurve: de målte punktene, med bue imellom. */
function glatt(raa: Punkt[]): Punkt[] {
  const malt = tynn(raa)
  if (malt.length < 3) return malt
  const p = malt.map(somXY)
  const ut: Punkt[] = []
  const legg = (x: XY) =>
    ut.push([Math.round(x.t), x.lat, x.lon, null, null, null])

  for (let i = 0; i < p.length - 1; i++) {
    const p1 = p[i]!
    const p2 = p[i + 1]!
    // Endene har ingen nabo å bøye etter, så naboen speiles ut.
    const p0 = i > 0 ? p[i - 1]! : speil(p1, p2)
    const p3 = i + 2 < p.length ? p[i + 2]! : speil(p2, p1)
    // Det målte punktet selv, med sin egen `cog` i behold.
    ut.push(malt[i]!)

    const n = Math.min(BUE_MAX, Math.floor(mellom(p1, p2) / BUE_M))
    if (n < 1) continue
    const t0 = 0
    const t1 = t0 + Math.sqrt(mellom(p0, p1))
    const t2 = t1 + Math.sqrt(mellom(p1, p2))
    const t3 = t2 + Math.sqrt(mellom(p2, p3))
    if (!(t1 > t0 && t2 > t1 && t3 > t2)) continue

    for (let k = 1; k <= n; k++) {
      const del = k / (n + 1)
      const u = t1 + (t2 - t1) * del
      const a1 = bland(p0, p1, (u - t0) / (t1 - t0))
      const a2 = bland(p1, p2, (u - t1) / (t2 - t1))
      const a3 = bland(p2, p3, (u - t2) / (t3 - t2))
      const b1 = bland(a1, a2, (u - t0) / (t2 - t0))
      const b2 = bland(a2, a3, (u - t1) / (t3 - t1))
      const c = bland(b1, b2, (u - t1) / (t2 - t1))
      // Klokka rett mellom naboene, ikke gjennom splinen: se over.
      legg({ ...c, t: p1.t + (p2.t - p1.t) * del })
    }
  }
  ut.push(malt[malt.length - 1]!)
  return ut
}

export function Sporet({ siteId, on, mmsi }: {
  siteId: string
  on: string
  /** Skipet kortet handler om. Uten det er vinduet hele døgnet. */
  mmsi?: number | null
}) {
  const q = useApi<LoggbokSpor>(
    `/v1/sites/${encodeURIComponent(siteId)}/loggbok/spor?on=${
      encodeURIComponent(on)}${mmsi != null ? `&mmsi=${mmsi}` : ''}`)

  if (q.cold) return <div className="spor"><Rows n={3} /></div>
  if (q.error) return <div className="spor"><ErrorBox error={q.error} /></div>
  if (!q.data) return null
  return <Panel d={q.data} />
}

function Panel({ d }: { d: LoggbokSpor }) {
  const tz = d.timezone
  const fokus = d.vessels.find((v) => v.mmsi === d.mmsi) ?? d.vessels[0] ?? null

  /* Spillehodet, delt mellom spilleren og kartet. Se `playhead.ts`: en vanlig
     muterbar peker framfor tilstand, fordi merkene tegnes seksti ganger i
     sekundet og ingenting skal rendres på nytt for at en båt skal flytte seg. */
  const hode = useRef(newPlayhead())
  hode.current.startEpoch = d.from
  hode.current.duration = Math.max(1, d.to - d.from)

  const [tilstand, setTilstand] = useState<PlayerState>('idle')
  /* Klokkeslettet som TEKST, og bare det. Oppdateres et par ganger i sekundet
     av løkka under; alt annet som beveger seg leser spillehodet direkte. */
  const [naa, setNaa] = useState(d.from)
  const [stumt, setStumt] = useState<string[]>([])

  const segmenter = useMemo<Segment[]>(
    () => d.media
      .filter((m) => m.start_utc)
      .map((m) => ({
        key: m.key, startUtc: Date.parse(m.start_utc!) / 1000,
        durationS: MINUTT_S, size: m.size, kind: m.kind,
      }))
      .sort((a, b) => a.startUtc - b.startUtc),
    [d.media])

  const oppdrag = d.media[0]?.mission_id ?? null

  const spiller = useRef<ReturnType<typeof createSegmentPlayer> | null>(null)
  useEffect(() => {
    if (!oppdrag || !segmenter.length) return
    const p = createSegmentPlayer({
      missionId: oppdrag, segments: segmenter,
      /* Spillerens klokke ER spillehodet mens den går. Uten dette ville kartet
         og lyden vært to klokker som driver fra hverandre, og båten ville stått
         et annet sted enn den vi hører. */
      onTime: (epoch) => {
        hode.current.t = epoch - d.from
        hode.current.playing = true
      },
      onState: (s) => {
        setTilstand(s)
        hode.current.playing = s === 'playing'
      },
      onUndecodable: (k) => setStumt((b) => (b.includes(k) ? b : [...b, k])),
    })
    spiller.current = p
    return () => { p.destroy(); spiller.current = null }
  }, [oppdrag, segmenter, d.from])

  /* Klokka står der noen satte den, også før noe har spilt. `t = 0` og
     «stoppet» er ulike tilstander -- playhead.ts sier hvorfor -- men her ER det
     et tidspunkt før avspilling: skinna peker på starten av besøket, og båten
     skal stå der. */
  if (hode.current.t === null) hode.current.t = 0

  const settKlokke = (epoch: number) => {
    const t = Math.max(d.from, Math.min(d.to, epoch))
    hode.current.t = t - d.from
    setNaa(t)
    spiller.current?.seek(t)
  }

  /* Teksten, et par ganger i sekundet. Egen løkke framfor å skrive fra
     `onTime`, fordi den kalles per bilde og et setState per bilde er nettopp
     den re-rendringen spillehodet finnes for å unngå. */
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = playheadEpoch(hode.current)
      if (t !== null) setNaa(Math.round(t))
    }, 400)
    return () => window.clearInterval(id)
  }, [])

  const spenn = Math.max(1, d.to - d.from)
  const andel = Math.min(1, Math.max(0, (naa - d.from) / spenn))

  /* Hvor det finnes lyd, som merker på skinna. Slått sammen til
     sammenhengende strekk: 720 minutter er 720 elementer, og et hull på ett
     minutt midt i et besøk er nettopp det leseren skal kunne se. */
  const strekk = useMemo(() => {
    const ut: { fra: number; til: number }[] = []
    for (const s of segmenter) {
      const siste = ut[ut.length - 1]
      if (siste && s.startUtc - siste.til <= MINUTT_S) {
        siste.til = s.startUtc + MINUTT_S
      } else ut.push({ fra: s.startUtc, til: s.startUtc + MINUTT_S })
    }
    return ut
  }, [segmenter])

  const kanSpille = segmenter.length > 0 && stumt.length < segmenter.length

  return (
    <div className="spor-flate">
      <Kart d={d} ph={hode.current} />

      <div className="spor-bunn">
        <div className="spor-styring">
          <button type="button" className="btn primary" disabled={!kanSpille}
            onClick={() => {
              if (tilstand === 'playing') spiller.current?.pause()
              else void spiller.current?.play()
            }}>
            {tilstand === 'playing' ? 'Pause' : 'Spill'}
          </button>
          <span className="spor-klokke num">{klokkeslett(naa, tz)}</span>
          <span className="spor-linje muted">
            {fokus
              ? <>
                  {fokus.name ?? `MMSI ${fokus.mmsi}`} · nærmest{' '}
                  {avstand(fokus.closest_m)} ·{' '}
                  {klokkeslett(fokus.track[0]?.[0] ?? d.from, tz)}–
                  {klokkeslett(
                    fokus.track[fokus.track.length - 1]?.[0] ?? d.to, tz)}
                </>
              : 'ingen AIS-posisjon lagret for dette besøket'}
          </span>
        </div>

        {/* Skinna. Et trykk hvor som helst flytter både båten og lyden dit:
            navigeringen er ETT sted og ikke to, fordi de er samme øyeblikk. */}
        <div className="spor-skinne" role="slider" tabIndex={0}
          aria-label="Tidspunkt i besøket"
          aria-valuemin={d.from} aria-valuemax={d.to} aria-valuenow={naa}
          aria-valuetext={klokkeslett(naa, tz)}
          onKeyDown={(e) => {
            const steg = e.shiftKey ? 600 : 60
            if (e.key === 'ArrowRight') { settKlokke(naa + steg); e.preventDefault() }
            if (e.key === 'ArrowLeft') { settKlokke(naa - steg); e.preventDefault() }
          }}
          onPointerDown={(e) => {
            const rekt = e.currentTarget.getBoundingClientRect()
            const flytt = (x: number) =>
              settKlokke(d.from + ((x - rekt.left) / rekt.width) * spenn)
            flytt(e.clientX)
            const paaFlytt = (ev: PointerEvent) => flytt(ev.clientX)
            const slipp = () => {
              window.removeEventListener('pointermove', paaFlytt)
              window.removeEventListener('pointerup', slipp)
            }
            window.addEventListener('pointermove', paaFlytt)
            window.addEventListener('pointerup', slipp)
          }}>
          {/* Der det finnes lagret lyd. Et hull er et faktum og tegnes som et
              hull -- det er den samme regelen spennspilleren spiller stillhet
              etter framfor å skjøte minuttene sammen. */}
          {strekk.map((s) => (
            <div key={s.fra} className="spor-lyd" style={{
              left: `${((s.fra - d.from) / spenn) * 100}%`,
              width: `${((s.til - s.fra) / spenn) * 100}%`,
            }} />
          ))}
          <Nivaakurve nivaa={d.levels} fra={d.from} til={d.to} />
          <div className="spor-gaatt" style={{ width: `${andel * 100}%` }} />
          <div className="spor-hode" style={{ left: `${andel * 100}%` }} />
        </div>

        <p className="fjernt spor-note">
          {d.levels.length > 0
            ? 'Kurven er hvor høyt det var, minutt for minutt — høy topp,'
              + ' mye lyd. Relativ: den viser hvor du bør lytte, ikke hvor'
              + ' mange desibel det var.'
            : 'ingen nivåer lagret for dette tidsrommet'}
          {!segmenter.length && ' · ingen lyd lagret i dette tidsrommet'}
          {d.media_truncated && ' · besøket er lengre enn lydlista rekker over'}
          {stumt.length > 0 && stumt.length < segmenter.length
            && ` · ${stumt.length} minutt lot seg ikke lese og spiller som`
               + ' stillhet, framfor å skjøtes bort'}
        </p>
      </div>
    </div>
  )
}

/* --------------------------------------------------------------- nivåkurven
 *
 * HVORFOR EN KURVE OG IKKE TALL.
 *
 * Ei tolv timers skinne er tolv timer identisk grønt, og det ene den som åpner
 * den vil vite er «hvor i dette er det noe å høre». Seks hundre minutter kan
 * ikke auditioneres. En form kan leses på et blikk og pekes på med en tommel.
 *
 * RELATIV, OG SIDEN SIER DET. Dette er nodens egen bredbånds-dBFS, som er et
 * nivå i opptaket og ikke et nivå i sjøen -- om det får sies absolutt er
 * calibration.py sin avgjørelse (R5.3), og her tas den ikke. Derfor ingen akse
 * og ingen tall: «høyere her enn der» er en påstand som ikke trenger
 * kalibrering, og det er hele påstanden.
 */
function Nivaakurve({ nivaa, fra, til }: {
  nivaa: [number, number][]; fra: number; til: number
}) {
  const d = useMemo(() => {
    if (nivaa.length < 2) return null
    const spenn = Math.max(1, til - fra)
    const db = nivaa.map((p) => p[1])
    const lo = Math.min(...db)
    const hi = Math.max(...db)
    /* Et helt flatt døgn ville delt på null og gitt NaN i et attributt, som
       ikke er en feil noe sted -- banen tegnes bare ikke, uten et ord. */
    const h = hi - lo < 1 ? 1 : hi - lo
    const punkt = (p: [number, number]) =>
      `${(((p[0] - fra) / spenn) * 100).toFixed(3)},${
        (100 - ((p[1] - lo) / h) * 100).toFixed(2)}`
    return `M0,100 L${nivaa.map(punkt).join(' L')} L100,100 Z`
  }, [nivaa, fra, til])

  if (!d) return null
  return (
    <svg className="spor-nivaa" viewBox="0 0 100 100"
      preserveAspectRatio="none" aria-hidden="true">
      <defs>
        {/* Toppen lysere enn bunnen. En høy topp blir da både høy OG lys, som
            er den samme opplysningen i to kanaler -- og den overlever å bli
            sett på skrå i sollys, som er der dette faktisk brukes. */}
        <linearGradient id="spor-nivaa-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C_SIGNAL} stopOpacity="0.72" />
          <stop offset="100%" stopColor={C_SIGNAL} stopOpacity="0.14" />
        </linearGradient>
      </defs>
      <path d={d} fill="url(#spor-nivaa-g)" />
    </svg>
  )
}

/* ------------------------------------------------------------------- kartet */

function Kart({ d, ph }: { d: LoggbokSpor; ph: Playhead }) {
  const el = useRef<HTMLDivElement>(null)
  const lerret = useRef<HTMLCanvasElement>(null)
  const map = useRef<any>(null)
  const [feilet, setFeilet] = useState(false)
  const [klar, setKlar] = useState(0)
  /** Merkene som flytter seg, med sporet sitt. Skrevet av effekten under og
   *  lest av animasjonsløkka; ingen tilstand, fordi ingenting skal rendres på
   *  nytt for at en båt skal flytte seg fire piksler. */
  const levende = useRef<{ mk: any; el: HTMLElement; skip: HTMLElement
                           spor: SporFartoy['track']
                           raa: SporFartoy['track'] }[]>([])

  /* Stimen, lest på nytt hvert bilde framfor lukket over. Se sverm.ts: dette er
     FISKEN og ikke vann -- vi har ingen hydrodynamikk på merdnivå, og partikler
     som drev mellom ringene ville vært en animasjon av en strøm ingen har målt,
     tegnet oppå et ekte satellittbilde av et ekte anlegg. */
  const bebodd = useMemo(() => new Set(d.occupied_pens), [d.occupied_pens])
  const svermNaa = useRef<Sverm[]>([])
  svermNaa.current = d.pens
    .filter((p) => p.lat != null && p.lon != null)
    .map((p) => ({
      penId: p.pen_id,
      lon: p.lon as number, lat: p.lat as number,
      radiusM: radiusM(p.circumference_m),
      /* INGEN BÅND. Loggboka farger ingenting etter lus, og en stim i
         luseladderens grønne ville sagt «denne ringen er under grensa» på en
         skjerm som ikke har spurt om lus i det hele tatt. Nøytral, som den
         stiplede ringen på lusekartet: bevegelse uten en påstand. */
      band: 0,
      // EN TOM RING ER STILL. Brakklegging blir stillhet, og det er den ene
      // regelen som gjør stimen verdt å tegne i det hele tatt.
      tom: !bebodd.has(p.pen_id),
      utelt: bebodd.has(p.pen_id),
    }))

  useEffect(() => {
    if (!TOKEN) { setFeilet(true); return }
    let dod = false
    let m: any
    void (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default
        await import('maplibre-gl/dist/maplibre-gl.css')
        if (dod || !el.current) return
        m = new maplibregl.Map({
          container: el.current,
          attributionControl: false,
          /* Satellitt, ikke gatekart, av samme grunn som ellers: motivet er
             vann. Et gatekart av en fjord er en tom blå flate, mens et anlegg
             leses som ei rekke merder du kjenner igjen. */
          style: {
            version: 8,
            sources: {
              satellite: {
                type: 'raster',
                tiles: [`https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${TOKEN}`],
                tileSize: 256,
                attribution: '© Mapbox · © OpenStreetMap',
              },
            },
            layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
          },
          center: [d.site.lon ?? 0, d.site.lat ?? 0],
          zoom: 14,
        })
        map.current = m
        m.addControl(new maplibregl.NavigationControl({ showCompass: false }),
                     'top-right')
        m.addControl(new maplibregl.AttributionControl({ compact: true }),
                     'bottom-right')
        /* Først når stilen er lastet: addSource på et kart som ennå ikke har
           lest stilen sin kaster «Style is not done loading», og fordi det skjer
           inne i en async-funksjon dør resten av løkka stille. */
        m.on('load', () => { if (!dod) setKlar((n) => n + 1) })
        m.on('error', (e: any) => {
          if (e?.error?.status === 401 || e?.error?.status === 403) setFeilet(true)
        })
      } catch {
        if (!dod) setFeilet(true)
      }
    })()
    return () => { dod = true; try { m?.remove() } catch { /* alt borte */ } }
  }, [])

  useEffect(() => {
    const m = map.current
    const c = lerret.current
    if (!m || !c || klar === 0) return
    return startSverm(c, m, () => svermNaa.current)
  }, [klar])

  useEffect(() => {
    const m = map.current
    if (!m || klar === 0) return
    const markorer: any[] = []
    const lag: string[] = []
    /* Kildene for seg. Å fjerne laget og la kilden ligge igjen er en feil som
       ikke merkes før effekten kjører en gang til -- og den gjør den hver gang
       `useApi` revaliderer -- og da kaster `addSource` «There is already a
       source with ID anlegg» inne i en async-funksjon som dør stille: ingen
       ringer, ingen spor, ingen ramme. */
    const kilder: string[] = []

    void (async () => {
      const maplibregl = (await import('maplibre-gl')).default
      const punkter: [number, number][] = []

      /* Ringene først, under sporene. Merdene er stedet spørsmålet handler om
         -- «lå de ved merd 4» -- og et spor tegnet under dem ville forsvunnet
         nettopp der det betyr mest. */
      const ringer = d.pens
        .map((p) => penFeature(p))
        .filter((f): f is NonNullable<typeof f> => f !== null)
      const flater = [
        ...ringer,
        ...d.structures.map((s) => structureFeature(s)),
      ]
      if (flater.length) {
        m.addSource('anlegg', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: flater },
        })
        m.addLayer({
          id: 'anlegg-flate', type: 'fill', source: 'anlegg',
          paint: { 'fill-color': C_RING, 'fill-opacity': 0.08 },
        })
        m.addLayer({
          id: 'anlegg-kant', type: 'line', source: 'anlegg',
          paint: { 'line-color': C_RING, 'line-width': 1.2,
                   'line-opacity': 0.75 },
        })
        lag.push('anlegg-flate', 'anlegg-kant')
        kilder.push('anlegg')
        for (const f of flater) {
          for (const c of f.geometry.coordinates[0]!) punkter.push(c)
        }
      }

      /* Anlegget som ett rødt punkt, BARE når ingen ring er tegnet. Ringene er
         den bedre opplysningen når de finnes -- de sier hvor merd 4 er, som er
         det spørsmålet handler om -- men et anlegg ingen har tegnet ennå er den
         vanlige tilstanden, og et kart uten noe å måle avstanden mot gjør
         sporet til en strek i blått. */
      if (!ringer.length && d.site.lat != null && d.site.lon != null) {
        const prikk = document.createElement('div')
        prikk.className = 'kart-anlegg'
        markorer.push(new maplibregl.Marker({ element: prikk })
          .setLngLat([d.site.lon, d.site.lat]).addTo(m))
      }

      /* Merdnummeret som tekst. En ring uten nummer er en ring; med nummer er
         den merd 4, og «de lå ved merd 4» er hele svaret kortet ber om. */
      for (const p of d.pens) {
        if (p.lat == null || p.lon == null) continue
        const nr = document.createElement('div')
        nr.className = 'spor-merd'
        nr.textContent = p.pen_no
        markorer.push(new maplibregl.Marker({ element: nr, anchor: 'center' })
          .setLngLat([p.lon, p.lat]).addTo(m))
      }

      for (const v of d.vessels) {
        const fokus = v.mmsi === d.mmsi
        // Én kurve, og den bærer både streken, ramma og båten -- se `glatt`.
        const sporet = glatt(v.track)
        const linje = sporet.map((p) => [p[2], p[1]] as [number, number])
        if (linje.length > 1) {
          const id = `spor-${v.mmsi}`
          m.addSource(id, {
            type: 'geojson',
            data: { type: 'Feature', properties: {},
                    geometry: { type: 'LineString', coordinates: linje } },
          })
          m.addLayer({
            id, type: 'line', source: id,
            paint: {
              /* Den kortet spør om er lys; de andre er kontekst. To båter
                 tegnet likt gjør bildet til et rot av streker, og spørsmålet
                 gjelder én av dem. */
              'line-color': fokus ? C_SIGNAL : C_DEMPET,
              'line-width': fokus ? 2.2 : 1.2,
              'line-opacity': fokus ? 0.95 : 0.5,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          })
          lag.push(id)
          kilder.push(id)
        }
        /* RAMMEN ER ANLEGGET OG DET SOM SKJEDDE VED DET, ikke hele seilasen.
         *
         * Første forsøk rammet fokusbåtens fulle døgnspor, og siden båten kom
         * inn fjorden ble ramma fire kilometer bred: sju merder som en flekk
         * oppe i hjørnet og resten åpent hav. Det er nøyaktig bildet `Kart.tsx`
         * ble skrevet for å unngå, og spørsmålet -- lå de ved merd 4 -- kan
         * ikke besvares fra det.
         *
         * Så bare punktene som var I NÆRHETEN får sette ramma. Innseilinga er
         * fortsatt tegnet og kartet lar seg zoome ut; den bare bestemmer ikke
         * hva du ser først. */
        if (fokus && d.site.lat != null && d.site.lon != null) {
          const midt = { lat: d.site.lat, lon: d.site.lon }
          for (const p of sporet) {
            const [e, n] = toLocal(midt, { lat: p[1], lon: p[2] })
            if (Math.hypot(e, n) <= RAMME_M) punkter.push([p[2], p[1]])
          }
        }

        const wrap = document.createElement('div')
        wrap.className = `kart-merke spor-merke${fokus ? ' fokus' : ''}`
        const skip = document.createElement('div')
        skip.className = `spor-skip${v.aqua_fleet ? ' akva' : ''}`
        skip.innerHTML = skipSvg(fokus ? 30 : 20)
        wrap.append(skip)
        if (fokus) {
          const navn = document.createElement('span')
          navn.className = 'kart-navn'
          navn.textContent = v.name ?? String(v.mmsi)
          wrap.append(navn)
        }
        wrap.title = `${v.name ?? v.mmsi} · nærmest ${
          Math.round(v.closest_m ?? 0)} m`

        const start = posisjon(sporet, d.from)
          ?? (sporet[0] ? [sporet[0][2], sporet[0][1]] as [number, number]
              : null)
        if (!start) continue
        const mk = new maplibregl.Marker({ element: wrap, anchor: 'center' })
          .setLngLat(start).addTo(m)
        markorer.push(mk)
        levende.current.push({ mk, el: wrap, skip, spor: sporet,
                              raa: v.track })
      }

      if (d.site.lat != null && d.site.lon != null) {
        punkter.push([d.site.lon, d.site.lat])
      }
      if (punkter.length > 1) {
        const lons = punkter.map((p) => p[0])
        const lats = punkter.map((p) => p[1])
        m.fitBounds(
          [[Math.min(...lons), Math.min(...lats)],
           [Math.max(...lons), Math.max(...lats)]],
          /* Ulik polstring oppe: topplinja med navnet og «Tilbake» ligger
             ABSOLUTT over kartet, så en jevn ramme legger den nordligste
             ringen under en knapp. Taket på zoomen står igjen fordi to båter
             som lå tett ellers fyller kartet med bare dem. */
          { padding: { top: 76, right: 44, bottom: 28, left: 44 },
            maxZoom: 16.5, duration: 0 })
      }
    })()

    return () => {
      levende.current = []
      for (const mk of markorer) { try { mk.remove() } catch { /* */ } }
      // Lagene først, så kildene: en kilde et lag fortsatt bruker kan ikke
      // fjernes, og «anlegg» bærer to lag.
      for (const id of lag) { try { m.removeLayer(id) } catch { /* */ } }
      for (const id of kilder) { try { m.removeSource(id) } catch { /* */ } }
    }
  }, [klar, d])

  /* Båtene går, mens klokka går.
   *
   * Utenfor sporets ender skjules merket helt framfor å parkeres i enden: en
   * båt tegnet ved merdene klokka 03 fordi den kom klokka 09 er en påstand om
   * hvor den var, og den er gal. Fraværet er det sanne svaret, og en tom sjø
   * klokka 03 er nettopp opplysningen. */
  useEffect(() => {
    let raf = 0
    const steg = () => {
      const t = playheadEpoch(ph)
      for (const l of levende.current) {
        const pos = t === null ? null : posisjon(l.spor, t)
        if (pos) {
          try { l.mk.setLngLat(pos) } catch { /* kartet er borte */ }
          l.el.style.visibility = ''
          const kurs = kursVed(l.spor, t!, l.raa)
          if (kurs != null) l.skip.style.transform = `rotate(${kurs}deg)`
        } else {
          l.el.style.visibility = 'hidden'
        }
        l.el.classList.toggle('gaar', ph.playing)
      }
      raf = requestAnimationFrame(steg)
    }
    raf = requestAnimationFrame(steg)
    return () => cancelAnimationFrame(raf)
  }, [ph, klar, d])

  if (feilet) {
    return (
      <div className="spor-kartramme">
        <p className="fjernt" style={{ padding: 16 }}>
          kartet kunne ikke lastes — sporet står i loggen
        </p>
      </div>
    )
  }
  return (
    <div className="spor-kartramme">
      <div className="spor-kart" ref={el} />
      {/* STIMEN. Et lerret over kartet: sju ringer med førti moter er 280
          bevegelige ting, og 280 DOM-noder omprojisert hvert bilde er et annet
          produkt. `pointer-events: none` -- fisken er et bilde av hva som står
          i ringen. */}
      <canvas className="merdsverm" ref={lerret} aria-hidden="true" />
    </div>
  )
}
